import { create } from 'zustand';
import { apiFetch } from '../api/httpClient.ts';
import { readStorage, writeStorage } from '../lib/storage.ts';
import {
  BR_FALLBACK,
  effectiveCountry,
  parseCountryConfig,
  parseCountryList,
  type CountryConfig,
} from './countryConfig.ts';
import { fetchCountrySuggestion } from './geoSuggestion.ts';
import { setRoundAnchorTimeZone } from '../game/roundLabel.ts';

const SELECTED_KEY = 'sorteia:country';
/**
 * The last country row the app resolved to, as the API sent it.
 *
 * Until `/api/v1/countries` answers, `country` is Brazil — so on a cold start
 * every screen an Argentine opened first painted `R$` and Brazilian grouping
 * for a beat, then repainted (2026-09-15, Milhar Hora). Remembering the row
 * means the first paint is already right. The cache only ever seeds the
 * store: it must match the persisted selection, and the live list replaces it
 * as soon as it arrives, so a changed registry row wins within one request.
 */
const CACHED_KEY = 'sorteia:countryConfig';

function cachedCountry(selectedCode: string | null): CountryConfig | null {
  if (!selectedCode) return null;
  try {
    const parsed = parseCountryConfig(JSON.parse(readStorage(localStorage, CACHED_KEY) || 'null'));
    return parsed && parsed.code === selectedCode ? parsed : null;
  } catch {
    return null;
  }
}

function rememberCountry(country: CountryConfig) {
  writeStorage(localStorage, CACHED_KEY, JSON.stringify(country));
}

type CountryState = {
  /** The country the auth screens act as — the selection, or a sensible default. */
  country: CountryConfig;
  /** Every active country, for the selector. */
  countries: CountryConfig[];
  /** The player's explicit choice, persisted across visits. Null until they pick. */
  selectedCode: string | null;
  /**
   * True once the country list has been fetched successfully.
   *
   * This is about *reaching the server*, not about certainty over which country
   * we are: one domain serves them all, so the country is chosen, never inferred.
   * Registration surfaces a warning only when this is false, i.e. we are rendering
   * a fallback because the lookup itself failed.
   */
  loaded: boolean;
  load: () => Promise<void>;
  /**
   * What Cloudflare guessed, held in memory only.
   *
   * Deliberately NOT written to `sorteia:country`: that key means "the player chose
   * this". Persisting a guess there would make one IP lookup impersonate an
   * explicit choice on every later visit — precedence 1 would permanently outrank
   * precedence 2 and changing networks could never correct it.
   *
   * One consequence: while only the suggestion is active, `country` (what the UI
   * shows) and the `X-Sorteia-Country` header every request carries
   * (`httpClient.ts`'s `activeCountryCode`, which reads `sorteia:country` directly)
   * can disagree. That header intentionally tracks the *persisted* selection, not
   * `store.country` — the header asserts a fact to the server, and a guess is not
   * one yet. The mismatch resolves itself the moment the player picks, or their
   * phone number is verified.
   */
  suggestedCode: string | null;
  loadSuggestion: () => Promise<void>;
  selectCountry: (code: string) => void;
  /**
   * Adopt `users.country_code` after sign-in, overriding whatever was selected.
   *
   * The opposite precedence from `i18nStore.adoptServerLocale`, and deliberately:
   * locale is a **preference**, so a local choice wins; country is a **fact about
   * the account**, so the server wins. It fixes the currency and payment rails and
   * cannot be changed.
   *
   * Without this, signing in by email or username — neither of which carries a
   * country — leaves the app on the country the player happened to pick for the
   * phone prefix. An Argentine account would then have its balance and stakes
   * formatted as BRL.
   */
  adoptAccountCountry: (code: string | null | undefined) => void;
};

let loadInFlight: Promise<void> | null = null;
let suggestionInFlight: Promise<void> | null = null;

const initialSelectedCode = readStorage(localStorage, SELECTED_KEY) || null;

export const useCountryStore = create<CountryState>((set, get) => ({
  country: cachedCountry(initialSelectedCode) || BR_FALLBACK,
  countries: [],
  selectedCode: initialSelectedCode,
  loaded: false,
  suggestedCode: null,
  load: () => {

    if (loadInFlight) return loadInFlight;

    loadInFlight = apiFetch<unknown>('/api/v1/countries', { method: 'GET' })
      .then((payload) => {
        const countries = parseCountryList(payload);
        if (!countries.length) {
          // No markets are currently enabled. Keep controls disabled.
          set({ countries, loaded: true });
          return;
        }
        set({ countries, loaded: true, country: effectiveCountry(countries, get().selectedCode, get().suggestedCode) });
      })
      .catch(() => {
        // No stale or hardcoded market may be used when discovery fails.
        set({ countries: [], loaded: false });
      })
      .finally(() => {
        loadInFlight = null;
      });

    return loadInFlight;
  },
  loadSuggestion: () => {
    if (get().selectedCode) return Promise.resolve();   // 玩家选过了,建议已经没有意义
    if (get().suggestedCode) return Promise.resolve();  // already have one, no need to ask again
    if (suggestionInFlight) return suggestionInFlight;

    // Same shape as `load`'s `loadInFlight`: App and AuthPage both call this on
    // mount, so without a guard here the geo lookup fires twice in production
    // and up to four times in dev (React.StrictMode double-invokes effects).
    // Idempotent, so not a correctness bug — but it doubles the network round
    // trip on the very screen this feature exists to speed up.
    suggestionInFlight = fetchCountrySuggestion()
      .then((suggested) => {
        if (!suggested || get().selectedCode) return;
        set({ suggestedCode: suggested, country: effectiveCountry(get().countries, null, suggested) });
      })
      .finally(() => {
        suggestionInFlight = null;
      });

    return suggestionInFlight;
  },
  selectCountry: (code) => {
    const next = String(code || '').trim().toUpperCase();
    if (!next || !get().countries.some(row => row.code === next)) return;
    writeStorage(localStorage, SELECTED_KEY, next);
    // Recompute even when the code is unchanged: the stored selection is read at
    // store construction, before the country list has loaded, so `country` can
    // still be the fallback while `selectedCode` already matches.
    set({ selectedCode: next, country: effectiveCountry(get().countries, next) });
  },
  adoptAccountCountry: (code) => {
    const next = String(code || '').trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(next)) return;
    // No early return when the code already matches `selectedCode`. The stored
    // selection is read at store construction, before the country list exists, so
    // `selectedCode` can already be 'AR' while `country` is still the fallback —
    // skipping the recompute there is exactly how an AR account kept showing R$.
    get().selectCountry(next);
  },
}));

/**
 * The gaming-day anchor follows the active country.
 *
 * Subscribing here rather than calling the setter from each mutation means no
 * future path that changes `country` — a new selector, a new adoption rule — can
 * forget to move `#001..#288` with it. Idempotent, so re-running on unrelated
 * state changes costs nothing.
 */
setRoundAnchorTimeZone(useCountryStore.getState().country.timezone);
useCountryStore.subscribe((state) => setRoundAnchorTimeZone(state.country.timezone));

// Remember the resolved row once it comes from the live list (never the
// fallback, never the cache echoing itself), so the next cold start paints the
// right currency first.
useCountryStore.subscribe((state) => {
  if (state.loaded && state.countries.length && state.selectedCode === state.country.code) rememberCountry(state.country);
});

/** Read the country outside React (payload builders, validators). */
export function activeCountry(): CountryConfig {
  return useCountryStore.getState().country;
}
